import "./style.css";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import Experience from "./Experience.jsx";
import { Loader, OrbitControls } from "@react-three/drei";
import { Suspense, useState, useRef } from "react";
import * as THREE from "three";
import {
  EffectComposer,
  Bloom,
  Vignette,
  SSAO,
} from "@react-three/postprocessing";
import { BlendFunction } from "postprocessing";
// import { useControls } from "leva";

// Three.js 0.153 uses "encodings_fragment" — postprocessing 6.28+ expects
// "colorspace_fragment". Polyfill it so the shader compiler can resolve it.
if (!THREE.ShaderChunk["colorspace_fragment"]) {
  THREE.ShaderChunk["colorspace_fragment"] =
    THREE.ShaderChunk["encodings_fragment"] ?? "";
}

// ── Camera states: exact position + rotation captured from debugger ───────────
const D = THREE.MathUtils.degToRad;
const makeState = (px, py, pz, rx, ry, rz) => ({
  position: new THREE.Vector3(px, py, pz),
  quaternion: new THREE.Quaternion().setFromEuler(
    new THREE.Euler(D(rx), D(ry), D(rz), "XYZ"),
  ),
});

// On narrow screens the zoom-in view is too close — pull back by ~70%
const isMobile = window.innerWidth <= 768;

const STATES = [
  makeState(-8.82, 4.88, 4.6, -46.7, -52.8, -40.2), // 0 — default overview
  isMobile
    ? makeState(-1, 1.2, 1.2, -10.8, -24.1, -1.5) // 1 — mobile zoom (shifted right)
    : makeState(-0.82, 0.99, 0.67, -10.8, -24.1, -1.5), // 1 — desktop zoom
  makeState(-2.23, 1.56, 3.85, -22.1, -28.2, -8.8), // 2 — one step back
];

// ── Free camera OrbitControls constraints (computed from debugger positions) ──
// minAzimuth: atan2(-43.46, -21.95) ≈ -2.04 rad  (left / behind limit)
// maxAzimuth: atan2(-16.79,  45.84) ≈ -0.35 rad  (rightward limit)
// maxDistance: sqrt(36.53²+27.11²+24.32²)         ≈ 52 units
// maxPolar: Math.PI/2                              (prevents dipping below floor)
const FREE_CAM = {
  minAzimuthAngle: -2.04,
  maxAzimuthAngle: -0.35,
  maxDistance: 40,
  minDistance: 3,
  maxPolarAngle: Math.PI / 2,
};

// ── Combined debug + camera controller ───────────────────────────────────────
function CameraSystem({ stateIndex, freeCamera }) {
  const { camera } = useThree();
  const currentQuat = useRef(STATES[0].quaternion.clone());
  const wasFreeCam = useRef(false);

  useFrame(() => {
    // Pause automated movement while free-cam mode is active
    if (freeCamera) {
      wasFreeCam.current = true;
      return;
    }

    // On first frame after exiting free-cam, re-sync quat from actual camera
    // so the transition lerps from where OrbitControls left off, not from a stale value
    if (wasFreeCam.current) {
      currentQuat.current.copy(camera.quaternion);
      wasFreeCam.current = false;
    }

    const state = STATES[stateIndex];
    camera.position.lerp(state.position, 0.05);
    currentQuat.current.slerp(state.quaternion, 0.05);
    camera.quaternion.copy(currentQuat.current);
  });

  if (freeCamera)
    return (
      <OrbitControls
        makeDefault
        minDistance={FREE_CAM.minDistance}
        maxDistance={FREE_CAM.maxDistance}
        minAzimuthAngle={FREE_CAM.minAzimuthAngle}
        maxAzimuthAngle={FREE_CAM.maxAzimuthAngle}
        maxPolarAngle={FREE_CAM.maxPolarAngle}
      />
    );
  return null;
}

// ── Shared overlay styles ─────────────────────────────────────────────────────
const centerBase = {
  position: "fixed",
  bottom: "2rem",
  left: "50%",
  transform: "translateX(-50%)",
  fontFamily: "monospace",
  letterSpacing: "2px",
  zIndex: 100,
};

const cornerBase = {
  position: "fixed",
  top: "2rem",
  right: "2rem",
  fontFamily: "monospace",
  letterSpacing: "2px",
  zIndex: 100,
};

const hintStyle = {
  ...centerBase,
  color: "#fff",
  fontSize: "0.8rem",
  opacity: 0.7,
  pointerEvents: "none",
};

const btnStyle = {
  ...centerBase,
  background: "rgba(0,10,0,0.85)",
  color: "#fff",
  border: "1px solid #fff",
  padding: "0.6rem 1.8rem",
  fontSize: "0.9rem",
  cursor: "pointer",
  borderRadius: "5px",
  boxShadow: "0 0 10px rgba(0, 251, 255, 0.30)",
};

const freeCamBtnStyle = {
  ...cornerBase,
  background: "#000",
  color: "#fff",
  border: "1px solid #fff",
  padding: "0.5rem",
  cursor: "pointer",
  borderRadius: "5px",
  boxShadow: "0 0 10px rgba(0, 251, 255, 0.30)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const CameraIcon = ({ size = 24, color = "#fff" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 800 800"
    width={size}
    height={size}
    fill="none"
    stroke={color}
    strokeWidth={50}
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    {/* Left reel */}
    <circle cx="280" cy="250" r="110" />
    <circle cx="280" cy="250" r="40" />
    {/* Right reel */}
    <circle cx="480" cy="250" r="110" />
    <circle cx="480" cy="250" r="40" />
    {/* Body */}
    <rect x="120" y="360" width="480" height="300" rx="10" />
    {/* Lens / viewfinder */}
    <polygon points="600,410 740,360 740,610 600,560" />
  </svg>
);

// ── Even-size CSS fix for Safari ──────────────────────────────────────────────
// Safari's CSS 3D transform engine produces incorrect results when the
// container has odd pixel dimensions. drei's <Html transform> inherits the
// canvas container size for its perspective/matrix calculations, so an odd
// width or height (e.g. iPhone 393px viewport) displaces the iframe.
// We use a pure-CSS approach: calc() rounds down to the nearest even number.
// See: https://github.com/mrdoob/three.js/issues/26950
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const evenSizeStyle = isSafari
  ? {
      // floor to even: 2 * floor(available / 2)
      // CSS round() is supported in Safari 15.4+, which covers all iOS 15+ devices
      width: "calc(2 * round(down, 50vw, 1px))",
      height: "calc(2 * round(down, 50svh, 1px))",
    }
  : { width: "100%", height: "100%" };

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [camState, setCamState] = useState(0);
  const [freeCamera, setFreeCamera] = useState(false);
  const handleZoomIn = () => {
    setFreeCamera(false); // exit free cam when zooming in
    setCamState(1);
  };

  const handleZoomOut = () => {
    setCamState((s) => (s === 1 ? 2 : 0));
  };

  const toggleFreeCamera = () => {
    setFreeCamera((f) => !f);
    setCamState(0); // always return to overview when toggling free cam
  };

  return (
    <>
      {/* Wrapper enforces even pixel dimensions on Safari — see evenSizeStyle */}
      <div style={evenSizeStyle}>
        <Canvas
          shadows
          // Cap DPR at 2 for performance on high-density screens (3× iPhones).
          // The Safari CSS3D transform fix is handled by the even-dimensions
          // wrapper above — see useEvenDimensions.
          dpr={[1, 2]}
          resize={{ scroll: false, debounce: { scroll: 50, resize: 0 } }}
          camera={{
            fov: 80,
            near: 0.1,
            far: 2000,
            position: [-8.82, 4.88, 4.6],
          }}
          gl={{
            toneMapping: THREE.ACESFilmicToneMapping,
            toneMappingExposure: 0.28,
          }}
        >
          <CameraSystem stateIndex={camState} freeCamera={freeCamera} />
          <Suspense fallback={null}>
            <Experience
              camState={camState}
              freeCamera={freeCamera}
              onZoomIn={freeCamera ? undefined : handleZoomIn}
            />
          </Suspense>

          {/* enableNormalPass gives SSAO access to the geometry normal buffer */}
          <EffectComposer enableNormalPass>
            {/* Ambient occlusion — darkens crevices/corners like Blender's path tracer.
              samples=32 is the minimum for grain-free results (16 produces visible noise). */}
            <SSAO
              blendFunction={BlendFunction.MULTIPLY}
              samples={32}
              radius={0.04}
              intensity={20}
              luminanceInfluence={0.5}
              color="black"
            />
            {/* Screen + matrix glow */}
            <Bloom
              intensity={0.4}
              luminanceThreshold={0.6}
              luminanceSmoothing={0.9}
              mipmapBlur
            />
            {/* Cinematic dark edges */}
            <Vignette
              offset={0.3}
              darkness={0.6}
              blendFunction={BlendFunction.NORMAL}
            />
          </EffectComposer>
        </Canvas>
      </div>

      {/* Centre-bottom: hint or zoom-out button */}
      {camState === 0 && !freeCamera && (
        <div style={hintStyle}>[ CLICK TO BEGIN ]</div>
      )}
      {camState >= 1 && (
        <button style={btnStyle} onClick={handleZoomOut}>
          {camState === 1 ? "↙ ZOOM OUT" : "⌂ OVERVIEW"}
        </button>
      )}

      {/* Top-right corner: free camera toggle — always visible */}
      <button style={freeCamBtnStyle} onClick={toggleFreeCamera}>
        {freeCamera ? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ display: "block" }}
          >
            <path d="M4 0 L4 20 L9.5 14.5 L15 22 L18 20.5 L12.5 13 L20 13 Z" />
          </svg>
        ) : (
          <CameraIcon size={22} color="#fff" />
        )}
      </button>

      <Loader />
    </>
  );
}
